/**
 * Save / Load Project — serialization wrapper for resuming downloads
 * without re-uploading the source XML.
 *
 * The wizard's pipeline produces a `RunMigrationPipelineResult` whose
 * download-relevant subset is captured here as a versioned `SavedProjectFile`.
 * The file is round-tripped through `JSON.stringify` and validated on load
 * against `migrationReportModelSchema` so a tampered or corrupt file is
 * rejected before any UI state is restored.
 *
 * NOTE: this file intentionally does NOT capture the original XML, the
 * parser output, mapping intermediates, or the rich `MigrationDocument`.
 * Only `renderModel` + `velocityJsonText` are persisted; downloads (the
 * browser-rendered HTML guide and the Velocity JSON) are derived from
 * those at load time.
 */

import { z } from 'zod';
import {
  migrationReportModelSchema,
  type MigrationReportModel,
} from '../../types/render-model.js';

/**
 * Bumped whenever the saved-project shape changes incompatibly. The
 * loader rejects files whose version it cannot recognize.
 *
 * v2 dropped the `selectedFormat` field when PDF/DOCX downloads were
 * archived in R2 (see docs/archived-components.md).
 */
export const PROJECT_FILE_SCHEMA_VERSION = 2 as const;

/**
 * Upper bound on the JSON payload accepted by `parseProject`. Diagrams
 * already cap at 8 MB aggregate per render model; 32 MB allows for some
 * envelope plus the velocity JSON text without becoming a memory hazard.
 */
export const MAX_PROJECT_FILE_BYTES = 32 * 1024 * 1024;

/**
 * Cap on the Velocity JSON text component. The generator's own output is
 * orders of magnitude smaller than this; the cap exists purely to bound
 * memory if a hostile file is fed back into the loader.
 */
const MAX_VELOCITY_JSON_TEXT_CHARS = 8 * 1024 * 1024;

const savedProjectFileSchema = z.object({
  projectFileSchemaVersion: z.literal(PROJECT_FILE_SCHEMA_VERSION),
  savedAtIso: z.string().min(1).max(40),
  renderModel: migrationReportModelSchema,
  velocityJsonText: z.string().max(MAX_VELOCITY_JSON_TEXT_CHARS),
  hasVelocityJson: z.boolean(),
});

export type SavedProjectFile = z.infer<typeof savedProjectFileSchema>;

export interface SerializeProjectInput {
  renderModel: MigrationReportModel;
  velocityJsonText: string;
  hasVelocityJson: boolean;
}

/**
 * Serializes a download-restorable snapshot of the wizard's pipeline
 * result. Returns a JSON string ready for a Blob download.
 */
export function serializeProject(input: SerializeProjectInput): string {
  const file: SavedProjectFile = {
    projectFileSchemaVersion: PROJECT_FILE_SCHEMA_VERSION,
    savedAtIso: new Date().toISOString(),
    renderModel: input.renderModel,
    velocityJsonText: input.velocityJsonText,
    hasVelocityJson: input.hasVelocityJson,
  };
  return JSON.stringify(file);
}

export type ParseProjectResult =
  | { ok: true; project: SavedProjectFile }
  | { ok: false; reason: 'too-large' | 'invalid-json' | 'wrong-version' | 'schema-invalid' };

/**
 * Parses and validates a previously-saved project file. The contract:
 *   - rejects payloads over `MAX_PROJECT_FILE_BYTES`
 *   - rejects malformed JSON
 *   - rejects unknown `projectFileSchemaVersion` values
 *   - rejects payloads that fail `migrationReportModelSchema`
 *
 * On success the returned `project` is safe to drive the wizard's Step 3
 * download buttons without invoking the parser/mapper/generator pipeline.
 */
export function parseProject(text: string): ParseProjectResult {
  if (text.length > MAX_PROJECT_FILE_BYTES) {
    return { ok: false, reason: 'too-large' };
  }
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, reason: 'invalid-json' };
  }
  // Cheap pre-check on the version field so we can return a more useful
  // rejection reason than the generic schema failure.
  if (
    raw !== null &&
    typeof raw === 'object' &&
    'projectFileSchemaVersion' in raw &&
    (raw as { projectFileSchemaVersion: unknown }).projectFileSchemaVersion !==
      PROJECT_FILE_SCHEMA_VERSION
  ) {
    return { ok: false, reason: 'wrong-version' };
  }
  const parsed = savedProjectFileSchema.safeParse(raw);
  if (!parsed.success) {
    return { ok: false, reason: 'schema-invalid' };
  }
  return { ok: true, project: parsed.data };
}
